export type BlogBlock =
  | { type: 'p'; text: string }
  | { type: 'h2'; text: string }
  | { type: 'h3'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'quote'; text: string; cite?: string }
  | { type: 'callout'; tone?: 'info' | 'warn' | 'success'; title?: string; text: string }
  | { type: 'code'; lang?: string; text: string };

export type BlogPost = {
  slug: string;
  title: string;
  excerpt: string;
  date: string; // ISO string
  author: string;
  readMinutes: number;
  tag: 'Product' | 'Coaching' | 'Guides' | 'Behind the build';
  body: BlogBlock[];
};

export const blogPosts: BlogPost[] = [
  {
    slug: 'why-we-built-baton',
    title: 'Why we built Baton (and what coaches keep telling us)',
    excerpt:
      "After a season of helping run a high-school T&F team out of three spreadsheets and a group text, we started building the workspace we wished existed.",
    date: '2026-04-22',
    author: 'The Baton team',
    readMinutes: 5,
    tag: 'Behind the build',
    body: [
      {
        type: 'p',
        text: "Last spring, a coach we know spent her Friday nights doing the same job, in the same order, every week. Open HyTek on a Windows laptop borrowed from the athletic department. Open Athletic.net in another tab to copy event entries. Open a Google Sheet to figure out who needed a ride. Open Outlook to email parents — one by one, because the kids with siblings on the team were getting two emails with conflicting times. By 11pm, she'd send a final \"sorry, ignore the last email\" message and go to bed.",
      },
      {
        type: 'p',
        text: "She is not unusual. She is the median track coach in 2026. Most coaches don't run meets — they run teams. And the tools built for them are either 20 years old, fundamentally for somebody else's job, or so generic they don't know what a heat is.",
      },
      {
        type: 'h2',
        text: "The three real jobs of a track coach (that nobody is solving)",
      },
      {
        type: 'p',
        text: "When we sat down with coaches in three states, the same three jobs came up over and over. None of them are 'enter results.' All of them happen between Wednesday and Saturday morning of every meet week.",
      },
      {
        type: 'ol',
        items: [
          "Decide who's running what, in what heat, in what lane — for a meet your team is attending, not hosting.",
          "Tell every parent exactly when and where to drop their kid off, what to bring, and when their event is — without sending the parent of triplets three separate emails.",
          "Keep season-long context: PRs, attendance, who's healthy, who's peaking, who's a flight risk to skip practice on Thursday.",
        ],
      },
      {
        type: 'p',
        text: "HyTek does job #1 only if you're the meet director. Athletic.net is great after the meet for results, less great before it. TeamSnap can send a group blast but doesn't know your athlete is registered in three events with a 47-minute gap between the long jump and the 4x100.",
      },
      {
        type: 'h2',
        text: 'What we decided to build',
      },
      {
        type: 'p',
        text: "Baton is a private workspace for a single team. Coaches register athletes into events on a meet, and the system knows everything that follows: who's racing, when, where, with whom, and which parent needs the schedule. One click sends a personalized email per family — deduped across siblings, with each kid's events listed in order with check-in windows.",
      },
      {
        type: 'callout',
        tone: 'info',
        title: 'Our design rule',
        text: "If a coach has to leave Baton to do something, that's a bug. Not a 'feature gap' — a bug.",
      },
      {
        type: 'h2',
        text: "What founding coaches keep asking for",
      },
      {
        type: 'p',
        text: "We ship every Friday based on what the founding cohort is wrestling with that week. Some patterns from the first hundred conversations:",
      },
      {
        type: 'ul',
        items: [
          "Bulk-import the roster from whatever spreadsheet I already have. (Done.)",
          "Let me build a meet from another team's published schedule, not retype it. (In progress.)",
          "Print a coach pack — one PDF with all my athletes, events, and seed times for Saturday. (Done.)",
          "Let parents acknowledge they got the email so I know who to text on Friday night. (Coming next.)",
          "Stop showing me the meet director features I don't need. (Always.)",
        ],
      },
      {
        type: 'p',
        text: "If you've coached a track team and any of this sounds familiar, get on the waitlist below. Founding coaches use Baton free for life — and they get to argue with us about what we ship next.",
      },
    ],
  },

  {
    slug: 'snake-seeding-explained',
    title: 'Snake seeding, explained: how to put the right kid in the right lane',
    excerpt:
      "Why fast kids end up in the middle, why heat 1 isn't always the slow heat, and a worked example you can run on your phone in 30 seconds.",
    date: '2026-04-15',
    author: 'The Baton team',
    readMinutes: 6,
    tag: 'Guides',
    body: [
      {
        type: 'p',
        text: "If you've ever stared at a stack of seed times the night before a meet and tried to figure out who goes in lane 4 of heat 2, you've discovered the small but real branch of math called heat seeding. The good news: you can do it in your head once you've seen the pattern. The better news: you can use our free tool and not do it in your head.",
      },
      {
        type: 'h2',
        text: 'Two questions, in this order',
      },
      {
        type: 'p',
        text: "Heat seeding is really two separate questions, and most people get it wrong by trying to answer them at the same time.",
      },
      {
        type: 'ol',
        items: [
          "Which heat does each athlete go in?",
          "Which lane does each athlete get inside that heat?",
        ],
      },
      {
        type: 'h2',
        text: 'Question 1: which heat?',
      },
      {
        type: 'p',
        text: "Sort everyone by seed time, fastest first. Then deal them out backward into heats — like dealing cards in reverse. The fastest athletes go in the LAST heat (the 'fast heat'), the next group goes in the second-to-last heat, and so on. Athletes with no time (NT) get sprinkled into the slow heats first.",
      },
      {
        type: 'p',
        text: "If you have 18 athletes and 8 lanes, you'll need 3 heats: 6 in heat 1, 6 in heat 2, 6 in heat 3. The fastest 6 go in heat 3.",
      },
      {
        type: 'callout',
        tone: 'info',
        title: 'Why heat 1 first?',
        text: "Slower heats run first so the meet builds toward a fast, exciting final heat. Spectators stay. Athletes warm up watching their teammates. The PA announcer has something to hype.",
      },
      {
        type: 'h2',
        text: 'Question 2: which lane?',
      },
      {
        type: 'p',
        text: "Inside each heat, lanes get assigned middle-out. The fastest athlete in the heat gets the middle lane (lane 4 or 5 on an 8-lane track). The next fastest gets the lane next to them. The slowest in the heat gets the outside lane.",
      },
      {
        type: 'p',
        text: "The order on an 8-lane track, by seed rank in the heat, is: 4, 5, 3, 6, 2, 7, 1, 8.",
      },
      {
        type: 'h3',
        text: 'Why middle-out?',
      },
      {
        type: 'ul',
        items: [
          "Middle lanes are physically the fastest in a curve — less centrifugal pull than lane 1, less open turn than lane 8.",
          "Middle-out lets the fastest athletes see and chase each other, which produces faster races and better photos.",
          "On a straight sprint (60m, 100m), it doesn't matter as much, but the convention holds for consistency.",
        ],
      },
      {
        type: 'h2',
        text: 'A worked example',
      },
      {
        type: 'p',
        text: "Say you have 10 athletes in the 100m and you're using 6 lanes (because that's what the host school chalked). You'll need 2 heats: 5 athletes per heat. Sort by seed time and 'snake' them: heat 2 gets seeds 1, 4, 5, 8, 9. Heat 1 gets seeds 2, 3, 6, 7, 10.",
      },
      {
        type: 'p',
        text: "Why snake? It balances the heats. If you just took the top 5 for heat 2 and the bottom 5 for heat 1, heat 2 would be a tight race and heat 1 would be a parade. Snake seeding spreads the talent so every heat has a leader.",
      },
      {
        type: 'p',
        text: "Then assign lanes middle-out inside each heat. On a 6-lane track that's 3, 4, 2, 5, 1, 6.",
      },
      {
        type: 'callout',
        tone: 'success',
        title: "Don't want to do this by hand?",
        text: "We built a free heat seeder you can use right now — paste your roster, pick your lane count, and it spits out the heats with lane assignments. No login required.",
      },
      {
        type: 'h2',
        text: "When to break the rules",
      },
      {
        type: 'p',
        text: "Almost never. The one exception coaches actually use: if two of your fastest kids are best friends and you want one of them in the slow heat to set a pace and grab a PR, swap them. The rest of the time, snake-seed and move on.",
      },
    ],
  },

  {
    slug: 'one-email-per-parent',
    title: 'The Saturday morning email: one schedule, every parent',
    excerpt:
      "What a parent actually needs at 6:30am on meet day, what they don't, and how Baton stops you from sending the parent of triplets three separate emails.",
    date: '2026-04-08',
    author: 'The Baton team',
    readMinutes: 4,
    tag: 'Product',
    body: [
      {
        type: 'p',
        text: "Ask any track parent what the worst part of the season is and you'll get a one-word answer: logistics. Not the early mornings, not the long days in the bleachers, not even the rain. The logistics. Where is the meet? When does my kid need to be there? When is her event? Are we eating between the long jump and the 200? Is there parking?",
      },
      {
        type: 'p',
        text: "The job of a coach is not to manage parent logistics. The job is coaching. But for one hour every Friday night, the coach becomes the parent's logistics coordinator — and it is the most thankless hour of the week.",
      },
      {
        type: 'h2',
        text: "What a parent actually opens on Saturday morning",
      },
      {
        type: 'p',
        text: "We sat with eight parents and watched them open meet emails on their phones. Every single one of them did the same three things, in the same order:",
      },
      {
        type: 'ol',
        items: [
          "Scrolled to find the venue address and tapped it to open Apple Maps or Google Maps.",
          "Scrolled to find their kid's first event and the check-in time.",
          "Closed the email.",
        ],
      },
      {
        type: 'p',
        text: "That's it. Nobody read the meet philosophy paragraph. Nobody clicked the team handbook PDF. Nobody studied the full event schedule. They wanted: where, when, what.",
      },
      {
        type: 'h2',
        text: "What we built",
      },
      {
        type: 'p',
        text: "Baton's parent email is short on purpose. The first 50 pixels are the venue address as a tappable link. The next block is your kid (or kids) and their events, in chronological order, each with a check-in window. Then a tiny footer with what to bring and your contact info as the coach.",
      },
      {
        type: 'callout',
        tone: 'info',
        title: 'The sibling problem',
        text: "If a family has two athletes on your roster, they get one email with both kids' schedules in chronological order — not two emails with conflicting timelines. This is the bug coaches complain about most and the one nobody else fixes.",
      },
      {
        type: 'h2',
        text: 'What we left out',
      },
      {
        type: 'p',
        text: "We deliberately don't include: the full meet program, the team logo banner, marketing for the booster club fundraiser, links to the parent handbook. These belong on a webpage or in a separate weekly newsletter, not in the meet-day email. The meet-day email is for one job: get your kid to the right place at the right time.",
      },
      {
        type: 'h2',
        text: 'How long it takes you',
      },
      {
        type: 'p',
        text: "Once your meet is built and your athletes are registered, sending the email is one click. Preview, send. The whole thing takes under a minute. The Friday night logistics hour goes away.",
      },
      {
        type: 'p',
        text: "Founding coaches tell us this is the feature parents notice first. Not the dashboard, not the rosters, not the PRs. The Saturday morning email that has the right information and nothing else.",
      },
    ],
  },

  {
    slug: 'hytek-athletic-net-baton',
    title: "HyTek vs Athletic.net vs Baton: who's actually for the coach?",
    excerpt:
      "Three tools, three jobs, three audiences. A clear-eyed look at what each one is built for — and why coaches end up using all three plus a spreadsheet.",
    date: '2026-04-01',
    author: 'The Baton team',
    readMinutes: 7,
    tag: 'Coaching',
    body: [
      {
        type: 'p',
        text: "If you coach track in 2026, you almost certainly use HyTek and Athletic.net. You probably also use a Google Sheet, a group text, and Outlook. None of them were designed for the same job, but they all live somewhere on your weekly task list. Here is what each one is actually for, and where the coach-shaped hole between them lives.",
      },
      {
        type: 'h2',
        text: 'HyTek Meet Manager',
      },
      {
        type: 'p',
        text: "HyTek is built for the meet director — the person hosting the meet. Its core job is taking entries from many teams, generating a heat sheet, talking to FinishLynx timing equipment, and producing official results. It is the gold standard for what it does, and it has earned its position over 30 years.",
      },
      {
        type: 'p',
        text: "It is also Windows-only, locally installed, licensed per-machine, and operated by someone who spent a weekend learning it. If you are not hosting the meet, HyTek is mostly irrelevant to your week. You don't run it. You receive heat sheets generated by it.",
      },
      {
        type: 'h3',
        text: 'Built for: meet directors. Not built for: team coaches.',
      },
      {
        type: 'h2',
        text: 'Athletic.net',
      },
      {
        type: 'p',
        text: "Athletic.net is best understood as a public results database with some registration features attached. After a meet, results get uploaded so the public can see who ran what. Coaches use it to find PRs, look up other teams' marks, and submit team entries to meets that accept Athletic.net registration.",
      },
      {
        type: 'p',
        text: "What it is not: a private workspace for your team. Anyone can see your athletes' results. Parents don't have a meaningful login experience there. There is no concept of 'send my parents the schedule for tomorrow.' It is a database with a registration form, not a team operating system.",
      },
      {
        type: 'h3',
        text: 'Built for: the public, and meet entry. Not built for: the daily life of a team.',
      },
      {
        type: 'h2',
        text: 'TeamSnap and the generic team apps',
      },
      {
        type: 'p',
        text: "Generic youth-sports apps know about practices, games, and group chats. They do not know what an event is. They do not know that a single athlete can be registered in five events at one meet, with check-in windows that do not align. A 'practice on Tuesday at 4pm' calendar item cannot represent 'long jump check-in 9:45, 100m heats at 11:20, 4x100 at 1:50.'",
      },
      {
        type: 'h3',
        text: "Built for: soccer and softball. Not built for: a sport with heats, lanes, and seven concurrent events.",
      },
      {
        type: 'h2',
        text: "The hole in the middle",
      },
      {
        type: 'p',
        text: "Notice what is missing. Nothing in this list is built for a track coach who manages a roster, registers athletes into events at meets they're attending, builds a coach pack for Saturday, and has to keep 40 sets of parents informed without going insane.",
      },
      {
        type: 'p',
        text: "That hole is what coaches fill with spreadsheets, group texts, and Friday-night Outlook marathons. That hole is what we built Baton to fill.",
      },
      {
        type: 'callout',
        tone: 'info',
        title: 'The honest comparison',
        text: "Baton does not replace HyTek if you host meets. It does not replace Athletic.net if you need official public results. It replaces the spreadsheet, the group text, and the Friday-night email session — which is where coaches actually spend their time.",
      },
      {
        type: 'h2',
        text: "How they fit together",
      },
      {
        type: 'ul',
        items: [
          "HyTek runs the meet you're attending — you don't touch it.",
          "Athletic.net hosts the public results and accepts entries for some meets — you submit through it.",
          "Baton runs your team Monday through Sunday — rosters, registrations, schedules, parent emails, season tracking.",
        ],
      },
      {
        type: 'p',
        text: "If you're spending more than 30 minutes on Friday nights doing logistics, you're in the hole these three tools leave behind. We built Baton for that hole. Get on the waitlist if it sounds familiar.",
      },
    ],
  },
];

export function getPostBySlug(slug: string): BlogPost | undefined {
  return blogPosts.find((p) => p.slug === slug);
}

export function getSortedPosts(): BlogPost[] {
  return [...blogPosts].sort((a, b) => (a.date < b.date ? 1 : -1));
}
